import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const getApiBaseUrl = () =>
  process.env.REACT_APP_API_URL || "http://localhost:8000";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token && { Authorization: `Token ${token}` }),
  };
};

// The backend (flat_api/pdf_parser.py) now normalizes check_in/check_out to
// ISO "YYYY-MM-DD" itself — it has to, since it supports both Booking.com's
// English date text and Airbnb's German date text, and only one place should
// own that locale-parsing logic. This is a defensive pass-through: if the
// value already looks like ISO, use it as-is; otherwise (older API version,
// or an unrecognized date the backend couldn't normalize) fall back to
// best-effort English-format parsing so the field doesn't silently stay
// empty. Parsed manually (no Date()) to avoid UTC offset shift.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = {
  Jan: "01", Feb: "02", Mar: "03", Apr: "04", May: "05", Jun: "06",
  Jul: "07", Aug: "08", Sep: "09", Oct: "10", Nov: "11", Dec: "12",
};
const parsePdfDate = (str) => {
  if (!str) return "";
  if (ISO_DATE_RE.test(str)) return str;

  // Month-before-day, e.g. "Tue, May 20, 2025" / "Tue May 20 2025"
  let m = str.match(/\w{3},?\s+([A-Za-z]{3})\s+(\d{1,2}),?\s+(\d{4})/);
  if (m && MONTHS[m[1]]) {
    return `${m[3]}-${MONTHS[m[1]]}-${m[2].padStart(2, "0")}`;
  }

  // Day-before-month, e.g. "Wed 1 Jul 2026" / "Wed, 1 Jul, 2026"
  m = str.match(/\w{3},?\s+(\d{1,2})\s+([A-Za-z]{3}),?\s+(\d{4})/);
  if (m && MONTHS[m[2]]) {
    return `${m[3]}-${MONTHS[m[2]]}-${m[1].padStart(2, "0")}`;
  }

  return "";
};

const UpdateBookingModal = ({ isOpen, onClose, booking, apartments, guests, onSaved, mode = "update" }) => {
  const { t } = useTranslation();
  const [apartmentId, setApartmentId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [guestId, setGuestId] = useState("");
  const [guestName, setGuestName] = useState("");
  const [source, setSource] = useState("");
  const [externalId, setExternalId] = useState("");
  const [totalPrice, setTotalPrice] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [allGuests, setAllGuests] = useState([]);
  // Drives the "Reading PDF…" indicator below the upload field. True only
  // while the parse-pdf request is in flight — cleared in the `finally`
  // block so it disappears whether parsing succeeded or failed, leaving
  // the (possibly pre-filled) form for the user to review before saving.
  const [isParsingPdf, setIsParsingPdf] = useState(false);

  // Populate form when booking changes (update mode) or reset for create mode
  useEffect(() => {
    if (booking) {
      setApartmentId(booking.apartment || "");
      setStartDate(booking.start_date || "");
      setEndDate(booking.end_date || "");
      setGuestId(booking.guest_id || "");
      setGuestName(booking.guest_name || "");
      setSource(booking.source || "");
      setExternalId(booking.external_id || "");
      setTotalPrice(booking.total_price != null ? String(booking.total_price) : "");
      setError("");
    } else {
      // Create mode — start with a blank form
      setApartmentId("");
      setStartDate("");
      setEndDate("");
      setGuestId("");
      setGuestName("");
      setSource("");
      setExternalId("");
      setTotalPrice("");
      setError("");
    }
  }, [booking, isOpen]);

  // Load guest list for the dropdown
  useEffect(() => {
    if (!isOpen) return;
    const loadGuests = async () => {
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/users/groups/guests/`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setAllGuests(data);
        }
      } catch (e) {
        console.error("Failed to load guests", e);
      }
    };
    loadGuests();
  }, [isOpen]);

  const isCreateMode = mode === "create";

  if (!isOpen) return null;
  if (!isCreateMode && !booking) return null;

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError("");
    setIsParsingPdf(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/bookings/parse-pdf/`, {
        method: "POST",
        headers: { Authorization: `Token ${localStorage.getItem("token")}` },
        body: formData,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to parse PDF");
      }
      const data = await res.json();

      // --- Duplicate check ---
      // Resolve apartmentId: current selection → only-one-apartment → booking_com_id lookup → name match
      const entries = Object.entries(apartments);
      let resolvedApartmentId = apartmentId || (entries.length === 1 ? entries[0][0] : null);

      if (!resolvedApartmentId && data.booking_com_property_id) {
        try {
          const aptsRes = await fetch(`${getApiBaseUrl()}/api/properties/apartments/`, {
            headers: getAuthHeaders(),
          });
          if (aptsRes.ok) {
            const apts = await aptsRes.json();
            const matched = apts.find(
              (a) => String(a.booking_com_id) === String(data.booking_com_property_id)
            );
            if (matched) resolvedApartmentId = String(matched.id);
          }
        } catch (_) { /* non-fatal */ }
      }

      if (!resolvedApartmentId && data.apartment_name) {
        const matched = entries.find(([, name]) =>
          name.toLowerCase().includes(data.apartment_name.toLowerCase()) ||
          data.apartment_name.toLowerCase().includes(name.toLowerCase())
        );
        if (matched) resolvedApartmentId = matched[0];
      }

      if (resolvedApartmentId && data.check_in && data.check_out) {
        const parsedStart = parsePdfDate(data.check_in);
        const parsedEnd   = parsePdfDate(data.check_out);

        if (parsedStart && parsedEnd) {
          const bookingsRes = await fetch(
            `${getApiBaseUrl()}/api/properties/apartments/${resolvedApartmentId}/bookings/`,
            { headers: getAuthHeaders() }
          );
          if (bookingsRes.ok) {
            const existing = await bookingsRes.json();
            const newStart = new Date(parsedStart);
            const newEnd   = new Date(parsedEnd);

            const conflict = existing.find((b) => {
              const bStart = new Date(b.start_date);
              const bEnd   = new Date(b.end_date);
              return newStart < bEnd && newEnd > bStart;
            });

            if (conflict) {
              setError(
                `A booking already exists for this apartment from ${conflict.start_date} to ${conflict.end_date}. ` +
                `Cannot import PDF.`
              );
              return;
            }
          }
        }
      }
      // --- End duplicate check ---

      if (data.guest_name) setGuestName(data.guest_name);
      if (data.source)     setSource(data.source);
      if (data.booking_number) setExternalId(data.booking_number);
      if (data.commissionable_amount) {
        const amount = data.commissionable_amount.replace(/[^0-9.,]/g, "").replace(",", ".");
        setTotalPrice(amount);
      }
      if (data.check_in)  setStartDate(parsePdfDate(data.check_in));
      if (data.check_out) setEndDate(parsePdfDate(data.check_out));
      // Auto-select apartment (resolved above — covers booking_com_id, single-apt, and name fallback)
      if (resolvedApartmentId && !apartmentId) setApartmentId(resolvedApartmentId);
    } catch (err) {
      setError("PDF parse failed: " + err.message);
    } finally {
      // Runs on every exit path (success, error, or the early return above)
      // — hides the "Reading PDF…" indicator and resets the file input so
      // the same file can be re-uploaded if needed.
      setIsParsingPdf(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);

    try {
      const payload = {
        guest: guestId ? parseInt(guestId, 10) : null,
        guest_name: guestName || null,
        apartment: parseInt(apartmentId, 10),
        start_date: startDate,
        end_date: endDate,
        source: source || null,
        external_id: externalId || null,
        total_price: totalPrice !== "" ? totalPrice : null,
      };

      const res = isCreateMode
        ? await fetch(`${getApiBaseUrl()}/api/bookings/`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload),
          })
        : await fetch(`${getApiBaseUrl()}/api/bookings/${booking.id}/`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify(payload),
          });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || data.message || JSON.stringify(data) || t("failed_to_create_update_booking"));
      }

      onSaved?.();
      onClose();
    } catch (err) {
      console.error(isCreateMode ? "Create booking error:" : "Update booking error:", err);
      setError(err.message || t("failed_to_create_update_booking"));
    } finally {
      setSaving(false);
    }
  };

  const guestDisplayName = booking
    ? (guests?.[booking.guest_id] ||
       allGuests.find((g) => g.id === booking.guest_id)?.username ||
       `Guest #${booking.guest_id}`)
    : "";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-lg">
        <h2 className="text-xl font-bold mb-4">
          {isCreateMode ? (t("create") || "Create") : t("update")} {t("booking") || "Booking"}
        </h2>

        {error && <p className="text-red-500 mb-4">{error}</p>}

        {/* PDF Upload (create mode only) */}
        {isCreateMode && (
          <div className="mb-5 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <label className="block text-blue-800 font-medium mb-1 text-sm">
              Import from PDF (Booking.com, Airbnb…)
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handlePdfUpload}
              disabled={isParsingPdf}
              className="block w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:cursor-pointer hover:file:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            {isParsingPdf ? (
              <div className="flex items-center gap-2 mt-2 text-blue-700 text-sm" role="status" aria-live="polite">
                <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Reading PDF… extracting booking details
              </div>
            ) : (
              <p className="text-xs text-gray-500 mt-1">
                Uploading a PDF pre-fills the fields below automatically.
              </p>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Booking ID (read-only, update mode only) */}
          {!isCreateMode && (
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">Booking ID</label>
              <input
                type="text"
                value={booking?.id}
                disabled
                className="w-full p-2 border border-gray-200 rounded-md bg-gray-100 text-gray-500"
              />
            </div>
          )}

          {/* Guest Name (free text — filled by PDF or typed manually) */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Guest Name</label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="e.g. Klaus Mustermann"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Guest (registered user — optional) */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">
              {t("guest_name")} <span className="text-gray-400 font-normal text-sm">(registered user, optional)</span>
            </label>
            <select
              value={guestId}
              onChange={(e) => setGuestId(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">-- {t("select")} --</option>
              {allGuests.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.first_name && g.last_name
                    ? `${g.first_name} ${g.last_name}`
                    : g.username}
                </option>
              ))}
              {/* Ensure current guest appears even if not in allGuests yet (update mode) */}
              {!isCreateMode && booking?.guest_id && !allGuests.find((g) => g.id === booking.guest_id) && (
                <option value={booking.guest_id}>{guestDisplayName}</option>
              )}
            </select>
          </div>

          {/* Apartment */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">{t("apartment_name")}</label>
            <select
              value={apartmentId}
              onChange={(e) => setApartmentId(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              required
            >
              <option value="">{t("select_apartment")}</option>
              {Object.entries(apartments).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date + End Date */}
          <div className="mb-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-1">{t("start_date")}</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-1">{t("end_date")}</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                required
              />
            </div>
          </div>

          {/* ── Manual Entry Section ── */}
          <div className="mb-4 mt-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide border-t pt-4">
              Manual Entry (External Platform)
            </h3>
          </div>

          {/* Source (channel) */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">Source / Channel</label>
            <select
              value={source}
              onChange={(e) => setSource(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value="">-- Select --</option>
              <option value="Booking.com">Booking.com</option>
              <option value="Airbnb">Airbnb</option>
              <option value="Direct">Direct</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Booking.com / External Booking Number */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">External Booking Number</label>
            <input
              type="text"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              placeholder="e.g. 5963285920"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Total Price */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium mb-1">{t("total_price") || "Total Price"} (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={totalPrice}
              onChange={(e) => setTotalPrice(e.target.value)}
              placeholder="e.g. 356.97"
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              disabled={saving || !apartmentId || !startDate || !endDate}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 disabled:opacity-50"
            >
              {saving
                ? `${t("saving") || "Saving"}...`
                : isCreateMode
                  ? (t("create") || "Create")
                  : t("update")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateBookingModal;
